import { useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import { parseWorkspaceRoute } from "@/utils/workspaceRoutes"

import type { PortalFlow } from "../types"
import {
  buildPortalLauncherHref,
  createPortalLaunchNonce,
} from "../utils/portalRedirect"

/** Which surface opened the portal — keep in sync with the call sites. */
type PortalSource = "settings_card" | "home_banner" | "shell_banner"

interface UseOpenBillingPortalResult {
  openPortal: (flow: PortalFlow, source?: PortalSource) => void
  pendingFlow: PortalFlow | null
  isPending: boolean
}

// How long the triggering button stays disabled after a launch, to swallow
// rapid double-clicks that would otherwise open multiple portal tabs.
const LAUNCH_DEBOUNCE_MS = 3000

export function useOpenBillingPortal(): UseOpenBillingPortalResult {
  const router = useRouter()
  const [pendingFlow, setPendingFlow] = useState<PortalFlow | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current)
      }
    },
    [],
  )

  const openPortal = useCallback(
    (flow: PortalFlow, source?: PortalSource) => {
      // Ignore re-clicks while a launch is in flight (see LAUNCH_DEBOUNCE_MS).
      if (debounceRef.current !== null) {
        return
      }
      setPendingFlow(flow)
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null
        setPendingFlow(null)
      }, LAUNCH_DEBOUNCE_MS)

      const slug =
        typeof router.query.slug === "string" ? router.query.slug : undefined
      const parsed = parseWorkspaceRoute(router.asPath ?? "")
      const workspaceId =
        parsed !== null && parsed.organizationSlug === slug
          ? parsed.workspaceId
          : undefined
      // Open the popup synchronously (dodges popup blockers) straight at the
      // same-origin launcher, which creates the portal session and redirects in
      // its own context. No `about:blank` handle to navigate after an async gap,
      // which is the step that left a dead blank popup on the first click. The
      // nonce lets the launcher confirm the launch came from this in-app click.
      const href = buildPortalLauncherHref(window.location.origin, {
        flow,
        source,
        slug,
        workspaceSlug: workspaceId,
        nonce: createPortalLaunchNonce() ?? undefined,
      })
      const popup = window.open(href, "_blank")
      if (popup === null) {
        // Popup blocked: send the current tab to the launcher instead.
        window.location.assign(href)
      }
    },
    [router.query.slug, router.asPath],
  )

  return {
    openPortal,
    pendingFlow,
    isPending: pendingFlow !== null,
  }
}
